import { useCallback } from 'react';
import DocumentOperation, { OperationState } from 'src/hooks/store/document/operations/abstract';
import { SetBlockOperation, SynchronousDocumentOperation } from 'src/hooks/store/document/operations';
import { useUserStore } from 'src/hooks/zustand/user';
import { track, Events } from 'src/utils/analytics';
import { useDocumentStore } from '../store/document';

// De-dupe the student_started_writing event per (session, documentId).
// Re-fires only on page reload, which is fine for funnel analysis.
const studentStartedWritingFired = new Set<string>();

/**
 * Return a function for applying the given operation to the document
 * in the global store. The operation knows the document ID to which it
 * must be applied. No document is returned; it should be accessed with
 * useDocument.
 */
export function useApplyDocumentOperation(localOnly = false) {
  const apply = useDocumentStore((state) => state.applyDocumentOperation);
  return useCallback(
    (operation: DocumentOperation): Promise<OperationState> => {
      maybeTrackStudentStartedWriting(operation);
      return apply(operation, localOnly);
    },
    [apply, localOnly],
  );
}

function maybeTrackStudentStartedWriting(operation: DocumentOperation) {
  // Only content-edit operations count as "writing".
  if (!(operation instanceof SetBlockOperation)) return;

  const documentId = operation.documentId;
  if (studentStartedWritingFired.has(documentId)) return;

  const user = useUserStore.getState().user;
  if (user?.role !== 'student') return;

  const doc = useDocumentStore.getState().documentsById[documentId];
  if (!doc) return;

  // Only fire if the document's creator is a different user (i.e., a teacher assignment).
  const docCreatorId = (doc as any).user?.id;
  if (!docCreatorId || docCreatorId === user.id) return;

  studentStartedWritingFired.add(documentId);
  track(Events.StudentStartedWriting, {
    document_id: documentId,
    creator_user_id: docCreatorId,
  });
}

/**
 * Return a function that synchronously applies the given operation
 * and returns the new document without updating the global store.
 * This is useful for previewing the result of an operation in a local part of the
 * UI before applying it, such when dragging and dropping blocks into a new
 * order. We want to preview the new order when dragging but not commit it
 * until dropped.
 *
 * The document against which the operation is applied must be in the global store
 * or the function returns undefined.
 */
export function usePreviewDocumentOperation() {
  return useCallback((operation: SynchronousDocumentOperation) => {
    const document = useDocumentStore.getState().documentsById[operation.documentId];
    if (!document) {
      return undefined;
    }

    return operation.apply(document);
  }, []);
}
